import {Alert, Anchor, Group, Loader, Select, Stack, Table, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {format} from 'date-fns'

import {useDispatchYards} from '../dispatch/use-dispatch'
import {ALL_YARDS} from '../dispatch/yards'
import {parseApiDate} from '../jobs/job-schedule-format'
import type {EquipmentException, WaitingForUnitNumber} from './schemas'
import {useEquipmentExceptions, useWaitingForUnitNumbers} from './use-equipment-checks'

type EquipmentExceptionsPageProps = {
    yardId: string | null
    onYardChange: (yardId: string | null) => void
}

function formatExceptionDay(value: string | null): string {
    return value ? format(parseApiDate(value), 'MMM d') : ''
}

function JobLink({jobId, label}: {jobId: string | null; label: string | null}) {
    if (!jobId || !label) {
        return <Text size="sm">{label ?? '—'}</Text>
    }

    return (
        <Anchor
            size="sm"
            renderRoot={(props) => <Link {...props} to="/jobs/$jobId" params={{jobId}} />}
        >
            {label}
        </Anchor>
    )
}

function ExceptionRow({row}: {row: EquipmentException}) {
    const {rentalAssetId} = row
    return (
        <Table.Tr>
            <Table.Td>
                <JobLink jobId={row.jobId} label={row.jobDisplayId} />
                {row.customerName ? (
                    <Text size="xs" c="dimmed">
                        {row.customerName}
                    </Text>
                ) : null}
            </Table.Td>
            <Table.Td>
                {rentalAssetId ? (
                    <Anchor
                        size="sm"
                        renderRoot={(props) => (
                            <Link
                                {...props}
                                to="/rental-assets/$assetId"
                                params={{assetId: rentalAssetId}}
                            />
                        )}
                    >
                        {row.equipment}
                    </Anchor>
                ) : (
                    <Text size="sm">{row.equipment}</Text>
                )}
            </Table.Td>
            <Table.Td>
                <Text size="sm">
                    {row.otherJobs.length > 0 ? 'Also on ' : row.detail}
                    {row.otherJobs.map((job, index) => (
                        <span key={job.jobId}>
                            {index > 0 ? ', ' : ''}
                            <JobLink jobId={job.jobId} label={job.displayId} />
                        </span>
                    ))}
                </Text>
            </Table.Td>
            <Table.Td>
                <Text size="sm">{formatExceptionDay(row.since)}</Text>
            </Table.Td>
        </Table.Tr>
    )
}

function formatExceptionCountsByYard(
    exceptions: EquipmentException[],
    yards: {id: string; name: string}[],
): string {
    const counts = yards.map((yard) => ({
        name: yard.name,
        count: exceptions.filter((row) => row.dispatchYardId === yard.id).length,
    }))
    const known = new Set(yards.map((yard) => yard.id))
    const noYard = exceptions.filter(
        (row) => row.dispatchYardId == null || !known.has(row.dispatchYardId),
    ).length
    const parts = counts.map((entry) => `${entry.name} ${entry.count}`)
    if (noYard > 0) parts.push(`No yard set ${noYard}`)
    return `By yard: ${parts.join(' · ')}`
}

function ExceptionsList({exceptions}: {exceptions: EquipmentException[]}) {
    if (exceptions.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No exceptions
            </Text>
        )
    }
    const groups = new Map<string, EquipmentException[]>()
    for (const row of exceptions) {
        groups.set(row.kindLabel, [...(groups.get(row.kindLabel) ?? []), row])
    }
    return (
        <Stack gap="lg">
            {[...groups.entries()].map(([label, rows]) => (
                <Stack key={label} gap="xs">
                    <Title order={4}>
                        {label} ({rows.length})
                    </Title>
                    <Table striped withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Job</Table.Th>
                                <Table.Th>Equipment</Table.Th>
                                <Table.Th>Details</Table.Th>
                                <Table.Th>Since</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((row, index) => (
                                <ExceptionRow
                                    key={`${row.jobId}-${row.equipment}-${index}`}
                                    row={row}
                                />
                            ))}
                        </Table.Tbody>
                    </Table>
                </Stack>
            ))}
        </Stack>
    )
}

function WaitingForUnitNumbersList({rows}: {rows: WaitingForUnitNumber[]}) {
    if (rows.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No items waiting for unit numbers
            </Text>
        )
    }
    return (
        <Table striped withTableBorder>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Job</Table.Th>
                    <Table.Th>Vendor</Table.Th>
                    <Table.Th>Item</Table.Th>
                    <Table.Th>Since</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows.map((row) => (
                    <Table.Tr key={row.rentalAssetId}>
                        <Table.Td>
                            <JobLink jobId={row.jobId} label={row.jobDisplayId} />
                            <Text size="xs" c="dimmed">
                                {row.customerName}
                            </Text>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm">{row.vendorName}</Text>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm">{row.typeName}</Text>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm">{formatExceptionDay(row.startDate)}</Text>
                        </Table.Td>
                    </Table.Tr>
                ))}
            </Table.Tbody>
        </Table>
    )
}

export function EquipmentExceptionsPage({yardId, onYardChange}: EquipmentExceptionsPageProps) {
    const {data: yards = []} = useDispatchYards()
    const exceptions = useEquipmentExceptions(yardId)
    const waiting = useWaitingForUnitNumbers(yardId)

    return (
        <Stack gap="xl">
            <Group justify="space-between" align="flex-end">
                <Stack gap={2}>
                    <Text size="sm">
                        Units on two jobs at once, rentals out too long or with no rate, equipment
                        still out after its job closed, automatic rentals not confirmed out,
                        equipment a job needs but does not have, bookings not on rent yet, and units
                        out of service past their expected-back date
                    </Text>
                    <Text size="sm" c="dimmed">
                        Equipment record checked against jobs
                        {exceptions.data
                            ? `, as of ${formatExceptionDay(exceptions.data.asOf)}`
                            : ''}
                    </Text>
                </Stack>
                <Select
                    label="Yard"
                    data={[
                        {value: ALL_YARDS, label: 'All yards'},
                        ...yards.map((yard) => ({value: yard.id, label: yard.name})),
                    ]}
                    value={yardId ?? ALL_YARDS}
                    onChange={(value) => onYardChange(!value || value === ALL_YARDS ? null : value)}
                    allowDeselect={false}
                    w={200}
                />
            </Group>

            <Stack gap="sm">
                <Title order={3}>
                    Exceptions
                    {exceptions.data ? ` (${exceptions.data.exceptions.length})` : ''}
                </Title>
                {exceptions.data && yardId == null && exceptions.data.exceptions.length > 0 ? (
                    <Text size="sm" c="dimmed">
                        {formatExceptionCountsByYard(exceptions.data.exceptions, yards)}
                    </Text>
                ) : null}
                {exceptions.isLoading ? (
                    <Loader size="sm" />
                ) : exceptions.error ? (
                    <Alert color="red" title="Could not load exceptions">
                        {exceptions.error.message}
                    </Alert>
                ) : (
                    <ExceptionsList exceptions={exceptions.data?.exceptions ?? []} />
                )}
            </Stack>

            <Stack gap="sm">
                <Title order={3}>Waiting for unit numbers</Title>
                <Text size="sm" c="dimmed">
                    Re-rented items on started jobs with no vendor unit number
                </Text>
                {waiting.isLoading ? (
                    <Loader size="sm" />
                ) : waiting.error ? (
                    <Alert color="red" title="Could not load the list">
                        {waiting.error.message}
                    </Alert>
                ) : (
                    <WaitingForUnitNumbersList rows={waiting.data ?? []} />
                )}
            </Stack>
        </Stack>
    )
}
